import api from './api';
import { DateTime } from 'luxon';

const state = {
	notification: { 0: [] },
	solutionCenterNotifications: { 0: [] },
	newNotification: {},
	notificationUpdate: 0,
	notificationStatusCount: {
		pending: 0,
		done: 0,
		ignored: 0,
	},
	lastUpdatedAbsence: [],
	updateNotificationQueue: [],
	updateNotificationTimeout: null,
	createNotificationQueue: [],
	createNotificationTimeout: null,
	markedAsDone: 0,
};

const mutations = {
	setNotificationCount(state, count) {
		state.notificationStatusCount = count;
	},
	setSolutionNotification(state, notifications) {
		let notify = { 0: [] };

		//Sort notification by creation date
		notifications.sort((a, b) => DateTime.fromISO(b.createdAt).diff(DateTime.fromISO(a.createdAt)));

		notifications.forEach((n) => {
			if (notify[n.priority]) {
				notify[n.priority].push(n);
			} else {
				notify[n.priority] = [];
				notify[n.priority].push(n);
			}
		});

		state.solutionCenterNotifications = notify;
		state.notificationUpdate = state.notificationUpdate + 1;
	},
	setNotifications(state, notifications) {
		let notify = { 0: [] };

		//Sort notification by creation date
		notifications.sort((a, b) => DateTime.fromISO(b.createdAt).diff(DateTime.fromISO(a.createdAt)));

		notifications.forEach((n) => {
			if (notify[n.priority]) {
				notify[n.priority].push(n);
			} else {
				notify[n.priority] = [];
				notify[n.priority].push(n);
			}
		});

		state.notification = notify;
		state.notificationUpdate = state.notificationUpdate + 1;
	},

	addNotifications(state) {
		for (const { notification, reverse } of state.createNotificationQueue) {
			if (!state.notification[notification.priority]) {
				state.notification[notification.priority] = [];
			}

			if (reverse) {
				state.notification[notification.priority].unshift(notification);
			} else {
				state.notification[notification.priority].push(notification);
			}

			if (notification?.params?.absence) {
				state.lastUpdatedAbsence.push(notification.params.absence);
			}
		}

		state.createNotificationQueue = [];
	},
	addSolutionNotifications(state) {
		for (const { notification, reverse } of state.createNotificationQueue) {
			if (!state.solutionCenterNotifications[notification.priority]) {
				state.solutionCenterNotifications[notification.priority] = [];
			}
			if (reverse) {
				state.solutionCenterNotifications[notification.priority].unshift(notification);
			} else {
				state.solutionCenterNotifications[notification.priority].push(notification);
			}
			if (notification?.params?.absence) {
				state.lastUpdatedAbsence.push(notification.params.absence);
			}
		}

		state.createNotificationQueue = [];
	},
	updateNotifications(state) {
		for (let priority in state.notification) {
			const updatedNotifications = state.notification[priority].map((n) => {
				for (const notification of state.updateNotificationQueue) {
					if (n.id == notification.id) {
						let newNotification = n;

						for (const key in notification) {
							newNotification[key] = notification[key];
						}

						if (notification?.params?.absence) {
							state.lastUpdatedAbsence.push(notification.params.absence);
						}

						return newNotification;
					}
				}

				return n;
			});
			state.notificationUpdate = state.notificationUpdate + 1;
			state.notification[priority] = updatedNotifications;

			if (Array.isArray(state.solutionCenterNotifications[priority])) {
				const filteredSolutionUpdates = updatedNotifications.filter((notification) => {
					const foundNotification = state.solutionCenterNotifications[priority].find(
						(solutionNotification) => solutionNotification.id == notification.id
					);
					return foundNotification !== undefined;
				});
				state.solutionCenterNotifications[priority] = filteredSolutionUpdates;
			}
		}
		state.updateNotificationQueue = [];

		state.notificationUpdate = state.notificationUpdate + 1;
	},

	setMarkedAsDone(state) {
		state.markedAsDone = state.markedAsDone + 1;
	},

	removeNotification(state, notificationId) {
		const removeFromCollection = (collection) => {
			for (const priority of Object.keys(collection)) {
				const index = collection[priority].findIndex((notification) => notification.id === notificationId);

				if (index > -1) {
					collection[priority].splice(index, 1);
					return true;
				}
			}
			return false;
		};

		const removedFromNotification = removeFromCollection(state.notification);
		const removedFromSolutionCenter = removeFromCollection(state.solutionCenterNotifications);

		if (removedFromNotification || removedFromSolutionCenter) {
			state.notificationUpdate++;
		}
	},

	clearLastUpdated(state) {
		state.lastUpdatedAbsence = [];
	},

	addNotificationToUpdateQueue(state, { timeout, notification }) {
		state.updateNotificationTimeout = timeout;
		state.updateNotificationQueue.push(notification);
	},

	addNotificationToCreateQueue(state, { timeout, notification, reverse }) {
		state.newNotification = notification;
		state.createNotificationTimeout = timeout;
		state.createNotificationQueue.push({ notification, reverse });
	},
};

const actions = {
	getNotifications({ rootState, commit }) {
		return api
			.getNotifications()
			.then((response) => {
				const notify = response.data;

				let filteredNotify = notify.filter((n) => {
					return n.recipients.includes(rootState.auth?.user?.id);
				});

				commit('setNotifications', filteredNotify);
			})
			.catch((err) => {
				console.log(err);
			});
	},
	async fetchNotification({ rootState, commit }, queryString) {
		try {
			const response = await api.fetchNotification(queryString);
			const notify = response.data.notifications;
			let filteredNotify = notify.filter((n) => {
				return n.recipients.includes(rootState.auth?.user?.id);
			});

			commit('setSolutionNotification', filteredNotify);
			return response.data;
		} catch (error) {
			console.log(error);
		}
	},

	async fetchNotificationCountPerStatus({ commit }, queryString) {
		try {
			const response = await api.fetchNotificationCountPerStatus(queryString);
			const count = response.data.statusCounts;
			commit('setNotificationCount', count);
		} catch (error) {
			console.log(error);
		}
	},

	async markNotifiactionAsRead({ rootState, commit }, { notificationId }) {
		try {
			let result = await api.setNotificationToRead(notificationId);
			return result;
		} catch (err) {
			console.log(err);
		}
	},

	/*--- Socket Events ---*/
	async socket_updated_Notification({ state, commit }, payload) {
		const notification = payload[0].payload;
		if (!notification) {
			return;
		}

		commit('clearLastUpdated');

		clearTimeout(state.updateNotificationTimeout);
		const timeout = setTimeout(() => {
			commit('updateNotifications');
		}, 200);
		commit('addNotificationToUpdateQueue', { timeout, notification });
		if (notification.done) {
			commit('setMarkedAsDone');
		}
	},

	async socket_created_Notification({ state, commit }, payload) {
		const notification = payload[0].payload;
		if (!notification) {
			return;
		}

		commit('clearLastUpdated');
		clearTimeout(state.createNotificationTimeout);
		const timeout = setTimeout(() => {
			commit('addNotifications');
			commit('addSolutionNotifications');
		}, 100);
		commit('addNotificationToCreateQueue', { timeout, notification, reverse: true });
	},

	async socket_deleted_Notification({ state, commit }, payload) {
		const notification = payload[0].payload;
		if (!notification) {
			return;
		}

		commit('clearLastUpdated');
		commit('removeNotification', notification.id);
	},
};

export default {
	namespaced: true,
	state,
	actions,
	mutations,
};
